import { I18n } from '@calendly/i18n';
import { Table, Header, HeaderCell, Body, Row, Cell, EmptyState as EmptyStateContainer, BatchCheckbox } from '@calendly/table';
import { Badge } from '@calendly/ui/components/badge';
import { FullStoryExclude, FullStoryMask } from '@calendly/ui/components/fullstory-privacy';
import { useTooltip } from '@calendly/ui/components/tooltip';
import { BodyText } from '@calendly/ui/components/typography';
import { VisuallyHidden } from '@calendly/ui/components/visually-hidden';
import { CheckboxField } from '@calendly/ui/form/checkbox-field';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NoGroup } from 'components/core/no_group';
import { ShowOnly } from 'components/core/show_only';
import { ORGANIZATION_INVITATION_PERMISSIONS } from 'constants/organization_invitation_permissions';
import { UserInfoAvatar } from 'features/user_profiles';
import { orgInvitationPermissionsSelector } from 'selectors/current_organization';
import { isAdminOrOwnerSelector, isOnlyGroupAdminSelector } from 'selectors/current_user';
import { ActionsDropdown } from './actions_dropdown';
import { sortOrderSelector, sortFieldSelector, filterTermSelector } from './selectors';
import { toggleSelection, togglePageSelection, sortInvitations } from './slice';
const tooltipCheckboxCss = "tl9fn4w";
const TooltipCheckbox = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(tooltipCheckboxCss, props.className)} />;
});
const noWrapCss = "nke33hy";
const NoWrap = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  return <Component ref={ref} {...restProps} className={classNames(noWrapCss, props.className)} />;
});
const invitationIsTabletDownCss = "i1vyguw5";
const invitationCss = "i1ijuhe1";
const Invitation = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isTabletDown = useDeviceType('isTabletDown');
  return <Component ref={ref} {...restProps} className={classNames(invitationCss, {
    [invitationIsTabletDownCss]: isTabletDown
  }, props.className)} />;
});
const InvitationRow = ({
  invitation,
  canManageSingleInvite,
  canManageBulkInvites
}) => {
  const dispatch = useDispatch();
  const toggleInvitation = () => dispatch(toggleSelection(invitation));
  const [CheckboxTooltip, tooltipHostProps] = useTooltip(I18n.t('errors.policy.no_permission'));
  return <Row>
      {canManageBulkInvites && <ShowOnly on="isDesktopUp">
          <Cell>
            <TooltipCheckbox {...!canManageSingleInvite ? tooltipHostProps : {}}>
              <CheckboxField checked={invitation.is_selected} onChange={() => toggleInvitation()} aria-label={I18n.t('publisher.components.table.select_item', {
            item: invitation.email
          })} disabled={!canManageSingleInvite} />
              <CheckboxTooltip />
            </TooltipCheckbox>
          </Cell>
        </ShowOnly>}
      <Cell>
        <Invitation>
          <FullStoryExclude>
            <UserInfoAvatar uuid={invitation.uuid} letter={invitation.avatar_letter} size="big" name={invitation.email} isPendingUser />
          </FullStoryExclude>
          <BodyText><FullStoryMask>{invitation.email}</FullStoryMask></BodyText>
        </Invitation>
      </Cell>
      <Cell label={I18n.t('publisher.organization.headers.status')}>
        <NoWrap>
          <FullStoryMask>
            {invitation.human_status && <Badge>{invitation.human_status}</Badge>}
          </FullStoryMask>
        </NoWrap>
      </Cell>
      <Cell label={I18n.t('publisher.organization.headers.group')}>
        <FullStoryMask>{invitation.user_group_membership && invitation.user_group_membership.group_name || <NoGroup />}</FullStoryMask>
      </Cell>
      <Cell label={I18n.t('publisher.organization.headers.date_invited')}>
        {invitation.brief_created_at}
      </Cell>
      <Cell label={I18n.t('publisher.organization.headers.date_last_sent')}>
        {invitation.brief_last_sent_at}
      </Cell>
      <Cell hasActions>
        {canManageSingleInvite && <ActionsDropdown invitation={invitation} />}
      </Cell>
    </Row>;
};
const EmptyState = () => {
  const filterTerm = useSelector(filterTermSelector);
  return <EmptyStateContainer>
      {filterTerm ? <>
          <BodyText weight="bold">{I18n.t('publisher.organization.empty.search')}</BodyText>
          <BodyText>{I18n.t('publisher.organization.empty.search_note_pending')}</BodyText>
        </> : I18n.t('publisher.organization.invitations.empty_state')}
    </EmptyStateContainer>;
};
const batchCheckboxStatus = ({
  invitations,
  canManageSingleInvite
}) => {
  const selectedInvitationsCount = invitations.filter(invitation => invitation.is_selected).length;
  const selectableInvitationsCount = invitations.filter(canManageSingleInvite).length;
  if (selectedInvitationsCount === 0) {
    return 'none';
  }
  if (selectedInvitationsCount === selectableInvitationsCount) {
    return 'all';
  }
  return 'some';
};
export const PendingUsersTable = ({
  invitations
}) => {
  const dispatch = useDispatch();
  const isAdminOrOwner = useSelector(isAdminOrOwnerSelector);
  const sortOrder = useSelector(sortOrderSelector);
  const sortField = useSelector(sortFieldSelector);
  const currentUserIsGroupAdminOnly = useSelector(isOnlyGroupAdminSelector);
  const orgInvitationPermissions = useSelector(orgInvitationPermissionsSelector);
  const sortedColumn = {
    name: sortField,
    direction: sortOrder
  };
  const canManageSingleInvite = invitation => invitation.policy.destroy || invitation.policy.resend;
  const canInviteAsGroupAdmin = currentUserIsGroupAdminOnly && [ORGANIZATION_INVITATION_PERMISSIONS.GROUP_ADMIN, ORGANIZATION_INVITATION_PERMISSIONS.USER].includes(orgInvitationPermissions) && invitations.find(invite => canManageSingleInvite(invite));
  const canInviteAsEndUser = !isAdminOrOwner && !currentUserIsGroupAdminOnly && orgInvitationPermissions === ORGANIZATION_INVITATION_PERMISSIONS.USER;
  const canManageBulkInvites = isAdminOrOwner || canInviteAsGroupAdmin || canInviteAsEndUser;
  const toggleInvitations = () => dispatch(togglePageSelection(canManageSingleInvite));
  return <div data-testid="pending-users">
      <Table sortedColumn={sortedColumn} onSort={column => dispatch(sortInvitations(column))}>
        <Header>
          {canManageBulkInvites && <ShowOnly on="isDesktopUp">
              <HeaderCell hugContent>
                <BatchCheckbox status={batchCheckboxStatus({
              invitations,
              canManageSingleInvite
            })} onChange={toggleInvitations} />
              </HeaderCell>
            </ShowOnly>}
          <HeaderCell columnName="email">
            {I18n.t('publisher.organization.headers.email')}
          </HeaderCell>
          <HeaderCell hugContent>
            {I18n.t('publisher.organization.headers.status')}
          </HeaderCell>
          <HeaderCell columnName="user_group_name">
            <NoWrap>{I18n.t('publisher.organization.headers.group')}</NoWrap>
          </HeaderCell>
          <HeaderCell hugContent columnName="created_at">
            {I18n.t('publisher.organization.headers.date_invited')}
          </HeaderCell>
          <HeaderCell hugContent columnName="last_sent_at">
            {I18n.t('publisher.organization.headers.date_last_sent')}
          </HeaderCell>
          <HeaderCell><VisuallyHidden>{I18n.t('publisher.components.table.action_menu')}</VisuallyHidden></HeaderCell>
        </Header>
        <Body>
          {invitations.map(invitation => <InvitationRow key={invitation.id} invitation={invitation} canManageSingleInvite={canManageSingleInvite(invitation)} canManageBulkInvites={canManageBulkInvites} />)}
          {invitations.length === 0 && <EmptyState />}
        </Body>
      </Table>
    </div>;
};

require("./table.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./table.js");